import React, { useCallback } from "react";
import {
  GoogleMap,
  DirectionsRenderer,
  Rectangle,
} from "@react-google-maps/api";

// Region corners type
interface RegionCorners {
  northwest: { lat: number; lng: number };
  northeast: { lat: number; lng: number };
  southwest: { lat: number; lng: number };
  southeast: { lat: number; lng: number };
}

// Props for the MapContainer component
interface MapContainerProps {
  directions: google.maps.DirectionsResult | null;
  currentRouteIndex: number;
  center: google.maps.LatLngLiteral;
  zoom: number;
  showRectangularCoverageArea?: boolean;
  regionCorners?: RegionCorners;
}

// Component to render the Google Map with directions
const MapContainer: React.FC<MapContainerProps> = ({
  directions,
  currentRouteIndex,
  center,
  zoom,
  showRectangularCoverageArea = false,
  regionCorners,
}) => {
  // Calculate rectangle bounds from region corners
  const rectangleBounds = regionCorners
    ? {
        north: regionCorners.northwest.lat,
        south: regionCorners.southwest.lat,
        east: regionCorners.northeast.lng,
        west: regionCorners.northwest.lng,
      }
    : null;

  // Fit map to region bounds on load
  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      if (rectangleBounds) {
        map.fitBounds(rectangleBounds, { top: 20, right: 20, bottom: 20, left: 20 });
      }
    },
    [rectangleBounds]
  );

  return (
    <div className="map-box" data-testid="map-container">
      <GoogleMap
        mapContainerStyle={{ height: "100%", width: "100%" }}
        center={center}
        zoom={zoom}
        onLoad={onMapLoad}
        options={{
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
      >
        {/* Rectangular Coverage Area */}
        {showRectangularCoverageArea && rectangleBounds && (
          <Rectangle
            bounds={rectangleBounds}
            options={{
              strokeColor: "#2E7D32",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: "#4CAF50",
              fillOpacity: 0.08,
            }}
          />
        )}

        {/* Directions */}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            routeIndex={currentRouteIndex}
            options={{
              suppressMarkers: false,
              polylineOptions: { strokeColor: "#1976D2", strokeWeight: 5 },
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default MapContainer;
